import React, { useState, useEffect } from "react";
import { ToastContainer } from "react-toastify";
import { User, Search } from "lucide-react";
import { Link } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import styles from "./CSS/MyFollowing.module.css";
import useAuthStore from "../../store/authStore";
import Footer from "../../common/UI/Footer";
import { useFollowing, useUnfollowUser } from "../../hooks/useFollowersHooks";
import Loading from "../../common/HousGlass Loader/Loading";
import { Helmet } from "react-helmet";

const AuthorList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 8; // Display 8 authors per page
  const { user } = useAuthStore();

  // Get following list using the custom hook
  const { data: followingData, isLoading, isError } = useFollowing(user?.id);

  // Get unfollow functionality
  const { mutate: unfollowUser, isLoading: isUnfollowLoading } =
    useUnfollowUser();

  // Extract the following array from the response
  const followedAuthors = followingData?.following || [];

  // Reset to page 0 when search term changes
  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  const handleUnfollow = (authorId, event) => {
    event.preventDefault();
    event.stopPropagation();

    // Call the unfollowUser mutation
    unfollowUser(authorId);
  };

  // Filter authors based on search term
  const filteredAuthors = followedAuthors.filter((author) =>
    author.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate total pages
  const totalPages = Math.ceil(filteredAuthors.length / pageSize);

  // Get the authors to display on the current page
  const displayedAuthors = filteredAuthors.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize
  );

  if (isLoading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.loadingContainer}><Loading/></div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.errorContainer}>
            Failed to load authors. Please try again.
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Following - SunoKahaniyan</title>
        <meta name="description" content="View and manage your following authors on SunoKahaniyan." />
      </Helmet>
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.profileImage}>
              <User className={styles.profileIcon} />
            </div>
            <h1 className={styles.name}>{user?.name}</h1>
            <div className={styles.searchContainer}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search authors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className={styles.searchIconWrapper}>
                <Search className={styles.searchIcon} />
              </div>
            </div>
          </div>

          {filteredAuthors.length === 0 ? (
            <div className={styles.emptyState}>
              {searchTerm
                ? `No authors found matching "${searchTerm}"`
                : "You're not following any authors yet."}
            </div>
          ) : (
            <div className={styles.authorGrid}>
              {displayedAuthors.map((author) => (
                <div key={author.id} className={styles.authorCard}>
                  <Link
                    to={`/author/${author.profile_id}`}
                    className={styles.authorInfo}
                  >
                    {/* Use a placeholder or default image since the API doesn't provide images */}
                    {author.profile_image ? (
                      <img
                        src={author.profile_image}
                        alt={author.name}
                        className={styles.authorImage}
                      />
                    ) : (
                      <div className={styles.authorImage}>
                        {author.name.charAt(0).toUpperCase()}
                      </div>
                    )}

                    <span className={styles.authorName}>{author.name}</span>
                  </Link>
                  <button
                    className={styles.unfollowButton}
                    onClick={(e) => handleUnfollow(author.id, e)}
                    disabled={isUnfollowLoading}
                  >
                    {isUnfollowLoading ? "Unfollowing..." : "Unfollow"}
                  </button>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                disabled={currentPage === 0}
                className={styles.paginationButton}
              >
                Previous
              </button>
              <span className={styles.paginationInfo}>
                Page {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
                }
                disabled={currentPage === totalPages - 1}
                className={styles.paginationButton}
              >
                Next
              </button>
            </div>
          )}

          <ToastContainer />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AuthorList;
